// Session row helpers, ported from the app (components/home/session_row.dart,
// components/performance/session_columns.dart, screens/profile_page.dart)
// so a session reads the same in every list: the Home row, the list column
// beside an open session and a profile's session rows.
import { forName, strHashBig } from "./people.js";
import { sessionTitle } from "./sessionName.js";

// SI-22: one live window everywhere, the server's PRESENCE_TTL_SECONDS.
export const LIVE_WINDOW_SECONDS = 50;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// The server's naive timestamps are UTC.
export function parseServerTime(value) {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" || !value) return null;
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

const nowOf = (now) => (now instanceof Date ? now : new Date());

// How many loops a row holds: a list row's library_count, else its library.
export function loopCount(row) {
  if (!row) return 0;
  if (Number.isFinite(Number(row.library_count)) && row.library_count != null) {
    return Number(row.library_count);
  }
  return Array.isArray(row.library) ? row.library.length : 0;
}

const loopsText = (n) => `${n} ${n === 1 ? "loop" : "loops"}`;

// A person as rows name them: their display name's first word when the row
// carries member_display_names, else the username with a capital.
export function personLabel(row, username) {
  const names = (row && row.member_display_names) || {};
  const display = typeof names[username] === "string" ? names[username].trim() : "";
  if (display) return display.split(/\s+/)[0];
  const u = String(username || "");
  return u ? u[0].toUpperCase() + u.slice(1) : u;
}

function members(row) {
  const out = [];
  for (const u of [row && row.creator, ...((row && row.users) || [])]) {
    if (u && !out.includes(u)) out.push(u);
  }
  return out;
}

// Who is in the session now: heartbeats under the live window.
export function sessionHere(row, now) {
  const t = nowOf(now).getTime();
  const uo = row && row.users_online;
  if (!uo || typeof uo !== "object") return [];
  const out = [];
  for (const [name, seen] of Object.entries(uo)) {
    const d = parseServerTime(seen);
    if (d && Math.abs(t - d.getTime()) < LIVE_WINDOW_SECONDS * 1000) out.push(name);
  }
  return out;
}

// How many are here, you included. The server's `here` (B1) wins.
export function hereCount(row, now) {
  if (row && Number.isFinite(row.here)) return row.here;
  return sessionHere(row, now).length;
}

// Live: someone other than you has a fresh heartbeat (SI-22). The server's
// `live` (B1) wins when it is sent, except that a session where you are the
// only one here is never live to you.
export function sessionIsLive(row, me, now) {
  if (!row) return false;
  const here = sessionHere(row, now);
  if (typeof row.live === "boolean") {
    if (!row.live) return false;
    return !(here.length && here.every((u) => u === me));
  }
  return here.some((u) => u !== me);
}

// B1's live_ended_at window: a session empty under 10 minutes "ended".
export const LIVE_ENDED_SECONDS = 600;

// A row's presence fields (B1) from a session doc: the doc's own here,
// live and live_ended_at when it carries them (a B1 doc omits
// live_ended_at when there is none), else derived from its users_online
// the way the server derives them. Rows prefer these over their
// heartbeats (hereCount, sessionIsLive), so a patch must carry them.
export function presenceFields(doc, now) {
  const d = doc || {};
  const server = Number.isFinite(d.here) || typeof d.live === "boolean";
  const here = Number.isFinite(d.here) ? d.here : sessionHere(d, now).length;
  const live = typeof d.live === "boolean" ? d.live : here > 0;
  let ended = null;
  if (server) ended = d.live_ended_at || null;
  else if (!here && d.users_online && typeof d.users_online === "object") {
    let newest = null;
    for (const seen of Object.values(d.users_online)) {
      const t = parseServerTime(seen);
      if (t && (!newest || t > newest.t)) newest = { t, seen };
    }
    if (newest && nowOf(now) - newest.t < LIVE_ENDED_SECONDS * 1000) ended = newest.seen;
  }
  return { here, live, live_ended_at: ended };
}

// What a session list row keeps from the open session's doc (Session.vue
// patches every list with it on a state_update, a rename, a privacy or
// picture change), so the list column and Mine never show stale rows,
// live badges included (M7).
export function sessionListFields(s, now) {
  const out = {
    alt_name: s.alt_name || "",
    privacy: s.privacy,
    private: s.private,
    picture: s.picture || "",
    blurb: s.blurb || "",
    users: s.users || [],
    users_online: s.users_online || {},
    favorited_by: s.favorited_by || [],
    library_count: (s.library || []).length,
    ...presenceFields(s, now),
  };
  if (s.member_display_names) out.member_display_names = s.member_display_names;
  return out;
}

// "now", "8 min ago", "18 h ago", "yesterday", "3 days ago", "last week",
// then "Oct 2" (the Home row).
export function sessionAge(value, now) {
  const t = parseServerTime(value);
  if (!t) return "";
  const n = nowOf(now);
  const mins = Math.floor((n - t) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  return `${MONTHS[t.getMonth()]} ${t.getDate()}`;
}

// The list column's time: "now", "14 min", "3 h" (same day), "Yesterday",
// a weekday under a week, then "Oct 9".
export function shortWhen(value, now) {
  const t = parseServerTime(value);
  if (!t) return "";
  const n = nowOf(now);
  const mins = Math.floor((n - t) / 60000);
  if (mins < 2) return "now";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24 && n.getDate() === t.getDate()) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 2) return "Yesterday";
  if (days < 7) return WEEKDAYS[t.getDay()];
  return `${MONTHS[t.getMonth()]} ${t.getDate()}`;
}

// A profile row's age: "today", "yesterday", "3 days ago", "last week",
// "2 weeks ago", "last month", "4 months ago", "last year", "2 years ago".
export function profileAge(value, now) {
  const t = parseServerTime(value);
  if (!t) return "";
  const days = Math.floor((nowOf(now) - t) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return "last month";
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  if (days < 730) return "last year";
  return `${Math.floor(days / 365)} years ago`;
}

// The Home row's second line: "8 loops · you, Maya, Theo · now"; nobody but
// you in it: "1 loop · 8 min ago". Others past three read "+N".
export function sessionSubtitle(row, me, now) {
  const parts = [loopsText(loopCount(row))];
  const people = members(row);
  const others = people.filter((u) => u !== me);
  if (others.length) {
    const names = [];
    if (me && people.includes(me)) names.push("you");
    names.push(...others.slice(0, 3).map((u) => personLabel(row, u)));
    const more = others.length - 3;
    parts.push(more > 0 ? `${names.join(", ")} +${more}` : names.join(", "));
  }
  const age = sessionAge(row && row.created_at, now);
  if (age) parts.push(age);
  return parts.join(" · ");
}

// The list column's second line: "8 loops · you, Maya, Theo", "1 loop ·
// public" when nobody else is in it and it is public, or "2 loops".
export function listSubtitle(row, me) {
  const loops = loopsText(loopCount(row));
  const others = members(row).filter((u) => u !== me);
  if (others.length) return `${loops} · ${["you", ...others.map((u) => personLabel(row, u))].join(", ")}`;
  if (!isPrivate(row)) return `${loops} · public`;
  return loops;
}

export function isPrivate(row) {
  if (!row) return false;
  if (typeof row.privacy === "string" && row.privacy) return row.privacy !== "public";
  const p = row.private;
  return typeof p === "string" ? p.trim().toLowerCase() === "true" : !!p;
}

// "public", "with you" (someone else's session you're in) or "invite only".
export function privacyWord(row, me) {
  if (!isPrivate(row)) return "public";
  if (me && row.creator !== me && (row.users || []).includes(me)) return "with you";
  return "invite only";
}

// A profile's session row: "4 loops · public · last month".
export function profileSubtitle(row, me, now) {
  return [loopsText(loopCount(row)), privacyWord(row, me), profileAge(row && row.created_at, now)]
    .filter(Boolean)
    .join(" · ");
}

// The row's title: the renamed title, a generated name as a date, else
// "Untitled".
export function rowTitle(row) {
  const name = row && typeof row.name === "string" ? row.name.trim() : "";
  const alt = row && typeof row.alt_name === "string" ? row.alt_name.trim() : "";
  if (!name && !alt) return "Untitled";
  return sessionTitle(row);
}

// The colour a row wears: yours violet, anyone else's their player colour.
export function ownerColor(row, me) {
  return forName((row && row.creator) || "", me);
}

// Seven rounded bars, heights hashed from the seed, in a 30 x 22 box (the
// app's SessionGlyphPainter). Returns an SVG path for a stroke of 2.2.
export function glyphPath(seed) {
  let h = Number(strHashBig(String(seed || "")) & 0x7fffffffn);
  const mid = 11;
  let d = "";
  for (let i = 0; i < 7; i++) {
    h = (Math.imul(h, 1103515245) + 12345) & 0x7fffffff;
    const a = 2 + ((h % 1000) / 1000) * 7.5;
    const x = 3 + i * 4;
    d += `M${x} ${(mid - a).toFixed(2)}V${(mid + a).toFixed(2)}`;
  }
  return d;
}

// The live badge's words: "Live · 3" (people here, you included), "Live"
// when the count is unknown.
export function liveBadgeText(row, now) {
  const n = hereCount(row, now);
  return n > 0 ? `Live · ${n}` : "Live";
}
